import type { PostHog } from "posthog-js";
import { getPostHog } from "./posthog";

/**
 * Router-level SPA pageview tracking (SD-10020).
 *
 * The SDK's own `'history_change'` pageviews are disabled (see posthog.ts):
 * they fire from a raw history listener — racing react-router's commit, so no
 * match data is available — and they carry raw URLs, where Mongo IDs fragment
 * "pages most/least used" into thousands of unique paths and query strings can
 * reference customers. This module subscribes to the data router instead and
 * captures `$pageview` AFTER a navigation commits, reporting the matched route
 * pattern (`/agents/:id`) — no IDs, no query, no hash.
 */

// Structural slices of react-router's Router/RouterState — just what pageview
// tracking reads. Lets tests drive the tracker with a plain object instead of
// constructing a real data router.
type RouteMatchLike = {
  route: { path?: string };
};

type RouterStateLike = {
  location: { key: string };
  matches: RouteMatchLike[];
};

type RouterLike = {
  state: RouterStateLike;
  subscribe(listener: (state: RouterStateLike) => void): () => void;
};

type PageviewAnalyticsClient = Pick<
  PostHog,
  "capture" | "has_opted_out_capturing" | "register"
>;
type GetPageviewAnalyticsClient = () => PageviewAnalyticsClient | null;

/**
 * Join the committed matches' `route.path` segments into the full route
 * pattern: param placeholders survive (`/agents/:id`), pathless layout/guard
 * routes contribute nothing, and absolute child paths (`agents/full/:id/*`)
 * flatten the same as relative ones. Returns `/` for a root-only match.
 */
export function routePatternFromMatches(matches: RouteMatchLike[]): string {
  const segments: string[] = [];
  for (const match of matches) {
    const path = match.route.path;
    if (!path) {
      continue;
    }
    segments.push(...path.split("/").filter(Boolean));
  }
  return `/${segments.join("/")}`;
}

// One pageview per committed navigation: react-router assigns each history
// entry a unique location.key, so re-renders/re-subscribes for the same entry
// (and captureCurrentPageview overlapping a subscribe fire) dedup here.
let lastCapturedLocationKey: string | null = null;

// The router being tracked, set by startPageviewTracking — module-level so
// captureCurrentPageview (called from the gating hook) reads the same state.
let trackedRouter: RouterLike | null = null;
let getPageviewAnalyticsClient: GetPageviewAnalyticsClient = getPostHog;

function capturePageview(state: RouterStateLike): void {
  const posthog = getPageviewAnalyticsClient();
  // Pre-init (key still being fetched from dale) or opted out (company gating):
  // the SDK would drop the event, so DON'T mark the location as captured —
  // otherwise the gating hook's captureCurrentPageview after opt-in would
  // dedup against a pageview that never actually recorded.
  if (!posthog || posthog.has_opted_out_capturing()) {
    return;
  }
  if (state.location.key === lastCapturedLocationKey) {
    return;
  }
  lastCapturedLocationKey = state.location.key;
  const pattern = routePatternFromMatches(state.matches);
  // Register the pattern as a super property so events captured between
  // navigations — especially autocaptured $rageclick/$dead_click, which only
  // carry the raw $pathname — report the same normalized, ID-free route as
  // pageviews. Frustration dashboards can then break down by route_pattern
  // without raw-path cardinality.
  posthog.register({ route_pattern: pattern });
  posthog.capture("$pageview", {
    // Override the SDK's window.location-derived URL/path with the normalized
    // pattern so pageview analytics aggregate by route, never by entity ID.
    $current_url: `${window.location.origin}${pattern}`,
    $pathname: pattern,
    route_pattern: pattern,
  });
}

/**
 * Subscribe pageview capture to the data router. Call once at startup (see
 * main.tsx). Fires only on navigations that commit — the initial page load is
 * instead captured by the gating hook via {@link captureCurrentPageview}, since
 * capture is opted-out until a company opts in.
 */
export function startPageviewTracking(
  router: RouterLike,
  getAnalyticsClient: GetPageviewAnalyticsClient = getPostHog,
): () => void {
  trackedRouter = router;
  getPageviewAnalyticsClient = getAnalyticsClient;
  return router.subscribe((state) => {
    capturePageview(state);
  });
}

/**
 * Capture a pageview for the route that is ALREADY committed — the subscriber
 * only sees future navigations. Called after company gating opts capture in,
 * so the page the user landed on counts. Location-key dedup makes it safe to
 * call alongside the subscription.
 */
export function captureCurrentPageview(): void {
  if (!trackedRouter) {
    return;
  }
  capturePageview(trackedRouter.state);
}

/**
 * The committed route pattern for the current location, or null before
 * tracking starts. Lets sibling analytics (refresh-rage) report the same
 * normalized, ID-free route naming as pageviews.
 */
export function getCurrentRoutePattern(): string | null {
  if (!trackedRouter) {
    return null;
  }
  return routePatternFromMatches(trackedRouter.state.matches);
}

/** Test-only: reset module state between cases. */
export function resetPageviewTrackingForTests(): void {
  lastCapturedLocationKey = null;
  trackedRouter = null;
  getPageviewAnalyticsClient = getPostHog;
}
